/* eslint-disable react/jsx-props-no-spreading */

import { Box, Flex, VStack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import AIAgentError from './AIAgentError';
import AIAgentIconShadow from './AIAgentIconShadow';
import AIAgentQueryInput from './AIAgentQueryInput';
import AIAgentService from './api/AIAgentService';
import AIAgentSuggestionGrid from './AIAgentSuggestionGrid';
import ExchangeContainer from './components/exchanges/ExchangeContainer';
import ExchangeListItem from './components/exchanges/ExchangeListItem';
import { GetConversation } from '@/client/services/api/graphql/gql/graphql';
import { useAIAgentStore } from './AIAgentStore';
import { useAuthStore } from '@/client/services/state/authStore';
import { useComponentSize } from 'react-use-size';
import { useListUserConversations } from './hooks/useListUserConversations';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export default function AIAgentInputForm() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const {
    currentConversationId,
    setCompletedTypingExchanges,
    setIsConversationActive,
    isConversationActive,
  } = useAIAgentStore();
  const conversationContainerRef = useRef<HTMLDivElement>(null);
  const { ref: parentRef, height: parentHeight } = useComponentSize();
  const exchangeRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Reset completed exchanges when conversation changes
  useEffect(() => {
    setCompletedTypingExchanges(new Set());
    // setIsConversationActive(false);
  }, [currentConversationId]);

  const { data: conversations } = useListUserConversations();

  const hasConversationHistory = conversations?.edges && conversations?.edges?.length > 0;

  const { data: conversation, isError } = useQuery({
    enabled: !!currentConversationId && currentConversationId !== 'INITIAL_CONVERSATION',
    queryKey: ['conversation', currentConversationId],
    queryFn: () => AIAgentService.getUserAIConversationById(currentConversationId),
    keepPreviousData: true,
  });

  // Calculate dynamic padding based on typing completion status
  const getDynamicPadding = () => {
    if (!currentConversationId || !conversation || conversation.length === 0) {
      return 0;
    }

    // Always use full padding during typing or when conversation is active
    // Only reduce padding when conversation is not active (typing completed)
    return isConversationActive ? parentHeight - 100 : Math.min(parentHeight - 100, 200);
  };

  // Track when conversation becomes active (has processing response)
  useEffect(() => {
    if (conversation && conversation.some((exchange) => exchange.id === 'PROCESSING_RESPONSE')) {
      setIsConversationActive(true);
    }

    if (conversation && conversation.length > 0) {
      // Add a small delay to ensure DOM elements are mounted and positioned
      setTimeout(() => {
        const lastExchange = exchangeRefs.current[conversation.length - 1];
        if (lastExchange && conversationContainerRef.current) {
          const container = conversationContainerRef.current;
          const lastExchangeTop = lastExchange.offsetTop;

          container.scrollTo({
            top: lastExchangeTop,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [conversation]);

  if (isError) return <AIAgentError />;

  return (
    <Flex
      ref={parentRef}
      flexDirection="column"
      height="100%"
      maxHeight="100%"
      position="relative"
      overflow="hidden"
    >
      <Box
        ref={conversationContainerRef}
        flex={1}
        overflowY="auto"
        minHeight={0}
        paddingBottom={getDynamicPadding()}
      >
        {!currentConversationId && (
          <VStack spacing={12} paddingBottom={8} minHeight="fit-content">
            <Box marginTop={currentConversationId || !hasConversationHistory ? 0 : '35%'}>
              <AIAgentIconShadow
                text={!hasConversationHistory ? t('aiAgent.hello') : ''}
                helpText={
                  !hasConversationHistory
                    ? t('aiAgent.ideas')
                    : `${t('aiAgent.userGreeting', { name: user.first_name })}`
                }
              />
            </Box>

            {!hasConversationHistory && <AIAgentSuggestionGrid />}
          </VStack>
        )}

        {conversation && conversation?.length > 0 && currentConversationId && (
          <ExchangeContainer>
            {conversation?.map((exchange: GetConversation, index: number) => {
              const isLatestExchange = index === conversation.length - 1;

              return (
                <ExchangeListItem
                  key={exchange.id}
                  ref={(el) => {
                    exchangeRefs.current[index] = el;
                  }}
                  exchange={exchange}
                  isLatestExchange={isLatestExchange}
                />
              );
            })}
          </ExchangeContainer>
        )}
      </Box>

      <Box position="absolute" bottom={0} left={0} right={0}>
        <AIAgentQueryInput />
      </Box>
    </Flex>
  );
}
